import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleNettoPoints } from "@/utils/formatUtils";
import { getTableSorting, sortableColumnProps } from "@/utils/tableUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Table, Tooltip, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  useDownloadChinaCompetitionResultsExport,
  useFilterChinaCompetitionResults,
} from "../../../../common/api/queries";
import { TableSizes } from "../../../../common/constants";
import { ExportFileType } from "../../../../common/enums";
import { AgentTypeTagWithName } from "../../../agent/components/tags/AgentTypeTagWithName";
import { AgentStatus } from "../../../agent/enums";
import { formatAgentIdNumber } from "../../../agent/utils";
import { useCurrentAgentAccount } from "../../../auth/hooks";
import { ChinaCompetitionResultOrderByColumn } from "../../enums";
import { ChinaCompetitionResult, ChinaCompetitionResultFilterPageRequest } from "../../types";

const COMPETITION_START_DATE = dayjs("2026-03-01");
const COMPETITION_END_DATE = dayjs("2026-08-31");

type Props = {
  searchParams: ChinaCompetitionResultFilterPageRequest;
  updateSearchParams: (params: ChinaCompetitionResultFilterPageRequest, append?: boolean, replace?: boolean) => void;
};

export const ChinaCompetitionTable = ({ searchParams, updateSearchParams }: Props) => {
  const { t } = useTranslation();

  const agentUserAccount = useCurrentAgentAccount();

  const { data, isFetching } = useFilterChinaCompetitionResults(
    searchParams,
    agentUserAccount?.agent.status === AgentStatus.BROKER_WITH_CONTRACT,
  );
  const downloadCompetitionResultsExport = useDownloadChinaCompetitionResultsExport();

  const columns: ColumnsType<ChinaCompetitionResult> = useMemo(() => {
    const items: ColumnsType<ChinaCompetitionResult> = [
      {
        key: "ranking",
        title: t("competition.attrs.ranking"),
        width: 60,
        align: "center",
        fixed: "left",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.RANKING, data),
        render: (_, record) => record.ranking,
      },
      {
        key: "agentName",
        title: t("common.agent"),
        width: 220,
        ellipsis: { showTitle: false },
        fixed: "left",
        render: (_, record) => (
          <AgentTypeTagWithName agent={record.agent} hideIdNumber ellipsis showLink={false} showRepresentatives />
        ),
      },
      {
        key: "agentStartEndPFA",
        width: 30,
        align: "center",
        fixed: "left",
        render: (_, record) => {
          const pfaEnd = record.agent.statusEndDate
            ? dayjs(record.agent.statusEndDate).isBetween(COMPETITION_START_DATE, COMPETITION_END_DATE, "days", "[]")
            : false;

          if (pfaEnd) {
            return (
              <Tooltip title={t("competition.helpers.pfaEndtHint")}>
                <span>
                  <AntIcon type="stop" />
                </span>
              </Tooltip>
            );
          }

          const pfaStart = record.agent.statusStartDate
            ? dayjs(record.agent.statusStartDate).isBetween(COMPETITION_START_DATE, COMPETITION_END_DATE, "days", "[]")
            : false;

          return pfaStart ? (
            <Tooltip title={t("competition.helpers.pfaStartHint")}>
              <span>
                <AntIcon type="up-circle" color="green" />
              </span>
            </Tooltip>
          ) : undefined;
        },
      },
      {
        key: "idNumber",
        title: t("agent.attrs.idNumber"),
        width: 80,
        fixed: "left",
        render: (_, record) => formatAgentIdNumber(record.agent),
      },
      {
        key: "commissionsSettingsLevel",
        title: t("agent.helpers.commissionsSettingsLevel"),
        width: 70,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.level?.code}</Ellipsis>,
      },
      {
        key: "commissionsSettingsRank",
        title: t("agent.helpers.commissionsSettingsRank"),
        width: 110,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.agent.commissionsSettings.rank?.name}</Ellipsis>,
      },
      {
        key: "batchPoints",
        title: t("competition.attrs.batchPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.BATCH_POINTS, data),
        render: (_, record) => {
          return (
            <Typography.Text type={numberOrZero(record.batchPoints) < 2400 ? "danger" : "success"} strong>
              {formatLocaleNettoPoints(record.batchPoints)}
            </Typography.Text>
          );
        },
      },
      {
        key: "nnPoints",
        title: t("competition.attrs.nnPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.NN_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.nnPoints),
      },
      {
        key: "koopPoints",
        title: t("competition.attrs.koopPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.KOOP_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.koopPoints),
      },
      {
        key: "iadRealtyPoints",
        title: t("competition.attrs.iadRealtyPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.IAD_REALTY_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.iadRealtyPoints),
      },
      {
        key: "iadIplusPoints",
        title: t("competition.attrs.iadIplusPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.IAD_IPLUS_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.iadIplusPoints),
      },
      {
        key: "ibisPoints",
        title: t("competition.attrs.ibisPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.IBIS_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.ibisPoints),
      },
      {
        key: "totalPartnerPoints",
        title: t("competition.attrs.totalPartnerPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.TOTAL_PARTNER_POINTS, data),
        render: (_, record) => (
          <Typography.Text type={numberOrZero(record.totalPartnerPoints) < 1200 ? "danger" : "success"} strong>
            {formatLocaleNettoPoints(record.totalPartnerPoints)}
          </Typography.Text>
        ),
      },
      {
        key: "educationPoints",
        title: t("competition.attrs.educationPoints"),
        width: 140,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.EDUCATION_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.educationPoints),
      },
      {
        key: "brokerPoints",
        title: t("competition.attrs.brokerPoints"),
        width: 140,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.BROKER_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.brokerPoints),
      },
      {
        key: "cancellationPoints",
        title: t("competition.attrs.cancellationPoints"),
        width: 120,
        align: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.CANCELLATION_POINTS, data),
        render: (_, record) => (
          <Typography.Text
            type={numberOrZero(record.cancellationPoints) < 0 ? "warning" : undefined}
            strong={numberOrZero(record.cancellationPoints) < 0}
          >
            {formatLocaleNettoPoints(record.cancellationPoints)}
          </Typography.Text>
        ),
      },
      {
        key: "totalPoints",
        title: t("competition.attrs.totalPoints"),
        width: 140,
        align: "right",
        fixed: "right",
        ...sortableColumnProps(ChinaCompetitionResultOrderByColumn.TOTAL_POINTS, data),
        render: (_, record) => formatLocaleNettoPoints(record.totalPoints),
      },
    ];

    return items;
  }, [data]);

  return (
    <>
      <div className={data?.pageData.length ? "table-header-export-actions" : "margin-bottom-small"}>
        <ActionTextIcon
          icon="download"
          color="blue"
          text={t("common.exportXlsx")}
          onClick={() =>
            downloadCompetitionResultsExport.mutate({ ...searchParams, exportFileType: ExportFileType.XLSX })
          }
        />

        <Divider orientation="vertical" />

        <ActionTextIcon
          icon="download"
          color="green"
          text={t("common.exportCsv")}
          onClick={() =>
            downloadCompetitionResultsExport.mutate({ ...searchParams, exportFileType: ExportFileType.CSV })
          }
        />
      </div>

      <Table<ChinaCompetitionResult>
        {...tableStandardProps()}
        loading={isFetching}
        columns={columns}
        scroll={{ x: TableSizes.XXL }}
        dataSource={data?.pageData}
        pagination={{
          ...data,
          ...paginationTableProps,
          current: numberOrZero(data?.pageIndex) + 1,
          pageSize: data?.pageSize,
          total: data?.totalElementsCount,
        }}
        onChange={(pagination, __, sorter) => {
          const { orderBy, orderDirections } = getTableSorting<ChinaCompetitionResultOrderByColumn>(sorter);

          updateSearchParams(
            { orderBy, orderDirections, pageIndex: pagination.current ? pagination.current - 1 : undefined },
            true,
            false,
          );
        }}
      />
    </>
  );
};
